import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Shared split-screen authentication layout for the 3 portals.
 *
 * Props :
 *  - accentColor   : Tailwind-ish hex used for accents on the left panel (e.g. '#D4A745')
 *  - accentLabel   : short text label shown above title (e.g. 'PORTAIL AGENCE')
 *  - title         : main heading on the left panel
 *  - tagline       : descriptive paragraph
 *  - benefits      : array of { icon: LucideIcon, label: string }
 *  - children      : form content (right column)
 *  - otherPortals  : array of { to, label, sub } (footer cross-links)
 *  - agencyLogoUrl : optional resolved logo URL
 *  - agencyName    : name shown next to the logo
 */
const AuthLayout = ({
  accentColor = '#D4A745',
  accentLabel = 'GAB\'S SERVICES',
  title = 'Bienvenue',
  tagline = '',
  benefits = [],
  children,
  otherPortals = [],
  agencyLogoUrl,
  agencyName = "GAB'S SERVICES HABITAT",
}) => {
  const [logoBroken, setLogoBroken] = React.useState(false);
  const showLogo = agencyLogoUrl && !logoBroken;
  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row" data-testid="auth-layout">
      {/* ===== Left brand panel ===== */}
      <div
        className="relative lg:flex-1 lg:min-h-screen flex flex-col justify-between overflow-hidden text-white px-8 py-10 sm:px-12 sm:py-14"
        style={{ backgroundColor: '#1B3A4B' }}
      >
        {/* Decorative pattern */}
        <BrandPattern accent={accentColor} />

        {/* Top: Logo & agency */}
        <div className="relative z-10 flex items-center gap-3">
          {showLogo ? (
            <div className="w-12 h-12 rounded-xl bg-white/95 p-1.5 shadow-lg flex-shrink-0">
              <img
                src={agencyLogoUrl}
                alt={agencyName}
                onError={() => setLogoBroken(true)}
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-[#1B3A4B] shadow-lg"
                 style={{ backgroundColor: accentColor }}>
              GS
            </div>
          )}
          <div className="leading-tight">
            <p className="text-white font-semibold text-sm tracking-wide">{agencyName}</p>
            <p className="text-white/60 text-[11px] uppercase tracking-widest">Agence immobiliere</p>
          </div>
        </div>

        {/* Middle: title + benefits */}
        <div className="relative z-10 max-w-md mt-12 lg:mt-0">
          <span
            className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] px-3 py-1 rounded-full mb-5"
            style={{ backgroundColor: `${accentColor}25`, color: accentColor, border: `1px solid ${accentColor}55` }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accentColor }} />
            {accentLabel}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
            {title}
          </h1>
          {tagline && (
            <p className="mt-4 text-white/70 text-sm sm:text-base leading-relaxed max-w-md">
              {tagline}
            </p>
          )}

          {benefits.length > 0 && (
            <ul className="mt-8 space-y-3">
              {benefits.map(({ icon: Icon, label }, idx) => (
                <li key={idx} className="flex items-start gap-3 text-white/85">
                  <span
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: `${accentColor}20`, color: accentColor }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-sm leading-relaxed">{label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer copyright */}
        <p className="relative z-10 text-white/40 text-xs mt-12 lg:mt-0">
          &copy; {new Date().getFullYear()} {agencyName}. Tous droits reserves.
        </p>
      </div>

      {/* ===== Right form panel ===== */}
      <div className="lg:flex-1 flex flex-col px-6 py-10 sm:px-10 sm:py-12 bg-white">
        <div className="w-full max-w-md mx-auto flex-1 flex flex-col justify-center">
          {children}
        </div>

        {/* Cross-portal links */}
        {otherPortals.length > 0 && (
          <div className="w-full max-w-md mx-auto mt-10 pt-6 border-t border-gray-100">
            <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold mb-3 text-center">
              Autres acces
            </p>
            <div className="grid grid-cols-2 gap-2">
              {otherPortals.map(p => (
                <Link
                  key={p.to}
                  to={p.to}
                  className="group flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-gray-200 hover:border-[#1B3A4B] hover:bg-[#1B3A4B]/5 transition-colors"
                  data-testid={`other-portal-${p.to.replace(/\W/g, '-')}`}
                >
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-[#1B3A4B] truncate">{p.label}</p>
                    {p.sub && <p className="text-[10px] text-gray-500 truncate">{p.sub}</p>}
                  </div>
                  <span className="text-[#D4A745] text-base leading-none">›</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const BrandPattern = ({ accent }) => (
  <>
    {/* Gradient overlay */}
    <div
      className="absolute inset-0 opacity-30"
      style={{
        background: `radial-gradient(circle at 80% 20%, ${accent}, transparent 50%), radial-gradient(circle at 20% 80%, ${accent}, transparent 40%)`,
      }}
    />
    {/* Soft geometric SVG */}
    <svg
      className="absolute -bottom-12 -right-12 w-[420px] h-[420px] opacity-[0.07] pointer-events-none"
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
          <path d="M 10 0 L 0 0 0 10" fill="none" stroke={accent} strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width="200" height="200" fill="url(#grid)" />
      <circle cx="60" cy="60" r="55" fill="none" stroke={accent} strokeWidth="0.5" />
      <circle cx="60" cy="60" r="80" fill="none" stroke={accent} strokeWidth="0.5" />
    </svg>
    {/* Subtle decorative bars at top */}
    <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: accent }} />
  </>
);

export default AuthLayout;
